// Shared visual components for Schedule module.
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
// FP3-035F Issue 2 — Preserve ?as_provider= across schedule clicks.
import useShadowNavigate from "@/lib/useShadowNavigate";
// FP3-035F Issue 2 — Hide Log Session buttons in read-only shadow mode.
import { useReadOnlyAsProvider } from "@/components/layout/ReadOnlyProviderBanner";
import { Warning, MapPin, ArrowRight, Trash, UsersThree, ClipboardText } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { providerBlockColor } from "@/lib/providerBlockColors";
import SessionStatusChip from "@/components/SessionStatusChip";
import LogSessionDialog from "@/components/log/LogSessionDialog";
import { UnavailableBadge, unavailMeta } from "@/components/schedule/UnavailableBadge";
import { localDateStr } from "@/lib/utils";
// FP3-108 — Canonical classroom-teacher resolver. The Builder
// Information popup MUST show the STUDENT'S teacher (from their
// Scheduling Profile), not the schedule owner/provider.
import { deriveTeacherDisplay } from "@/lib/teacherIdentity";
import {
  HOURS,
  TOTAL_MIN,
  TIME_START,
  TIME_END,
  SNAP,
  GRADE_COLORS,
  GRADE_ORDER,
  fmtTime,
  fmtHour,
  pct,
  gc,
  toMin,
  toTime,
} from "./utils";

/**
 * FP3-121 — Grade legend.
 *
 * Renders the K–12 palette. When `grades` is passed, only those
 * grades are shown (keeps the legend narrow when a provider's
 * caseload only spans a few grades). When omitted, the full K–12
 * palette is rendered so the operator can see the intended color
 * for any grade they might add.
 */
export function GradeLegend({ grades } = {}) {
  const shown = Array.isArray(grades) && grades.length > 0
    ? GRADE_ORDER.filter((g) => grades.includes(g))
    : GRADE_ORDER;
  return (
    <div className="flex items-center gap-3 flex-wrap" data-testid="grade-legend">
      {shown.map((g) => {
        const c = GRADE_COLORS[g];
        if (!c) return null;
        return (
          <div key={g} className="flex items-center gap-1.5" data-testid={`grade-legend-${g}`}>
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: c.bg, border: `1.5px solid ${c.border}` }}
            />
            <span className="text-[11px] text-[#6B7280]">{`Gr ${g}`}</span>
          </div>
        );
      })}
    </div>
  );
}

// Renders 5-minute tick marks across the canvas + hour and quarter labels.
export function TimeHeader({ leftWidth = 80, rightWidth = 0 }) {
  return (
    <div className="flex border-b border-[#d4d2cc]" data-testid="time-header">
      <div className="shrink-0 bg-[#ECEAE4] border-r border-[#d4d2cc]" style={{ width: leftWidth }} />
      <div className="flex-1 relative h-12 bg-[#ECEAE4]">
        {/* Hour labels + minute ticks */}
        {HOURS.map((h) => {
          const left = pct(h * 60);
          if (left < 0 || left > 100) return null;
          return (
            <div key={h}>
              {/* Hour label */}
              <div className="absolute top-0 flex flex-col items-start" style={{ left: `${left}%` }}>
                <span className="text-[10px] font-bold text-[#1F2937] heading-font whitespace-nowrap ml-0.5 leading-none mt-1">
                  {fmtHour(h)}
                </span>
              </div>
            </div>
          );
        })}
        {/* Minute ticks every 5 minutes */}
        {(() => {
          const ticks = [];
          for (let m = TIME_START; m <= TIME_END; m += SNAP) {
            const left = pct(m);
            if (left < 0 || left > 100) continue;
            const minute = m % 60;
            const isHour = minute === 0;
            const isQuarter = minute === 15 || minute === 45;
            const isHalf = minute === 30;
            const showLabel = !isHour; // we already show hour above
            ticks.push(
              <div key={m} className="absolute bottom-0" style={{ left: `${left}%` }}>
                <div
                  className={`w-px ${
                    isHour ? "h-3.5 bg-[#4B5563]" : isHalf ? "h-2.5 bg-[#6B7280]" : isQuarter ? "h-2 bg-[#9B9890]" : "h-1.5 bg-[#C9C7C1]"
                  }`}
                />
                {showLabel && (isQuarter || isHalf) && (
                  <span className="text-[8px] text-[#6B7280] absolute left-0.5 -bottom-3 whitespace-nowrap">
                    {String(minute).padStart(2, "0")}
                  </span>
                )}
              </div>
            );
          }
          return ticks;
        })()}
      </div>
      {rightWidth > 0 && <div className="shrink-0 bg-[#ECEAE4] border-l border-[#d4d2cc]" style={{ width: rightWidth }} />}
    </div>
  );
}

export function BuilderBlock({
  session,
  laneCount,
  conflictSet,
  validationSet,
  onResize,
  onDragItemStart,
  onDelete,
  provider,
  groupIds,
  isArmedGroup,
  onArmGroup,
  shiftHeldRef,
  shiftHeld,
  applyGroupMove,
  setDragPreview,
  highlightBlockId,
}) {
  const sm = toMin(session.start_time);
  const em = toMin(session.end_time);
  const dur = em - sm;
  // Iter 24 — provider-owned personal blocks (lunch / prep / etc.) get a
  // neutral palette so they don't read as student service blocks.
  const isPersonal = session.block_kind === "personal";
  const color = isPersonal
    ? providerBlockColor(session.personal_kind)
    : gc(session.students);
  const isConflict = conflictSet.has(session.id);
  // Iter 33d Follow-up 6 — Builder validation: amber dashed ring on
  // blocks flagged by post-save health-check (school-rule overlap,
  // attendance window, provider availability, teacher/student
  // constraint, cross-provider, etc.). Distinct from the red ring
  // which is reserved for hard double-bookings (conflictSet).
  const isValidationFlagged =
    validationSet &&
    (validationSet.has(session.id) ||
      (session._recurring_block_id && validationSet.has(session._recurring_block_id)));
  const laneH = 100 / laneCount;
  const memberIds = Array.isArray(groupIds) && groupIds.length > 0 ? groupIds : [session.id];
  const isInGroup = memberIds.length > 1 && !isPersonal;
  // Iter 24 — monthly cadence indicator
  const wom = session.weeks_of_month;
  const cadenceLabel =
    Array.isArray(wom) && wom.length > 0 && wom.length < 4
      ? `${wom.length} of 4 weeks`
      : null;
  const label = isPersonal
    ? (session.personal_label || session.personal_kind || "Personal")
    : session.students?.length > 0
    ? session.students.map((s) => s.first_name).join(", ")
    : session.service_type || "Open";

  const navigate = useShadowNavigate();
  const resizingRef = useRef(false);

  const handleResizeStart = (e) => {
    if (!onResize) return;
    e.stopPropagation();
    e.preventDefault();
    resizingRef.current = true;
    // Find dropzone parent (the row's flex-1 div with onDrop)
    const block = e.currentTarget.parentElement; // BuilderBlock root
    const dropzone = block?.parentElement;
    if (!dropzone) return;

    const onMove = (ev) => {
      const rect = dropzone.getBoundingClientRect();
      const rawMin = ((ev.clientX - rect.left) / rect.width) * TOTAL_MIN + TIME_START;
      const snapped = Math.round(rawMin / SNAP) * SNAP;
      const minEnd = sm + 10;
      const newEndMin = Math.max(minEnd, Math.min(snapped, TIME_END));
      onResize(session.id, !!session._isNew, toTime(newEndMin));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      // Defer reset to next tick so any synthesized dragstart still sees the flag.
      setTimeout(() => {
        resizingRef.current = false;
      }, 50);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  // Custom mouse-driven drag for Shift+drag group moves. macOS Chrome
  // suppresses native HTML5 dragstart when Shift is held on a draggable
  // element (treats it as an extend-selection gesture), so we take over with
  // mouse events the moment a Shift+left-mousedown lands on a grouped block.
  // Plain (non-shift) drags continue to use HTML5 D&D unchanged.
  const handleMouseDown = (e) => {
    if (e.button !== 0) return; // left button only
    if (resizingRef.current) return;
    if (!isInGroup) return;
    const shiftHeldNow = e.shiftKey || !!shiftHeldRef?.current;
    if (!shiftHeldNow) return; // let native dragstart handle non-shift
    if (typeof applyGroupMove !== "function") return;

    e.preventDefault();
    e.stopPropagation();

    const blockEl = e.currentTarget;
    blockEl.style.opacity = "0.35";

    let lastDayIdx = null;
    let lastStartMin = null;

    const findDropzone = (cx, cy) => {
      const els = document.elementsFromPoint(cx, cy);
      for (const el of els) {
        const tid = el.getAttribute && el.getAttribute("data-testid");
        if (tid && tid.startsWith("builder-drop-")) {
          const idx = parseInt(tid.slice("builder-drop-".length), 10);
          if (!Number.isNaN(idx)) return { el, dayIdx: idx };
        }
      }
      return null;
    };

    const onMove = (ev) => {
      const hit = findDropzone(ev.clientX, ev.clientY);
      if (!hit) {
        if (setDragPreview) setDragPreview(null);
        lastDayIdx = null;
        lastStartMin = null;
        return;
      }
      const rect = hit.el.getBoundingClientRect();
      const rawMin = ((ev.clientX - rect.left) / rect.width) * TOTAL_MIN + TIME_START;
      const snapped = Math.round(rawMin / SNAP) * SNAP;
      const startMin = Math.max(TIME_START, Math.min(snapped, TIME_END - dur));
      lastDayIdx = hit.dayIdx;
      lastStartMin = startMin;
      if (setDragPreview) setDragPreview({ dayIdx: hit.dayIdx, startMin, dur });
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      blockEl.style.opacity = "1";
      if (setDragPreview) setDragPreview(null);
      if (lastDayIdx !== null && lastStartMin !== null) {
        const ns = toTime(lastStartMin);
        const ne = toTime(lastStartMin + dur);
        applyGroupMove(memberIds, lastDayIdx, ns, ne);
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const blockEl = (
    <div
      draggable
      onMouseDown={handleMouseDown}
      onDragStart={(e) => {
        if (resizingRef.current) {
          e.preventDefault();
          return;
        }
        // Group move triggers: Shift+drag OR right-click "Move as group" armed.
        // `e.shiftKey` is unreliable on macOS Safari/Chrome during dragstart,
        // so we also consult shiftHeldRef which is mirrored from window
        // keydown/keyup. Resize is always single-block (handled separately).
        const shiftAtStart = e.shiftKey || !!shiftHeldRef?.current;
        const wantsGroupMove = isInGroup && (shiftAtStart || isArmedGroup);
        const payload = {
          type: "move",
          id: session.id,
          dur,
          isNew: !!session._isNew,
          // Always carry the full group roster — the drop handler decides
          // whether to apply a group move based on fresh shift state at
          // drop time. This is more robust than locking the decision in at
          // dragstart, where modifier-key state is browser-quirky.
          memberIds,
          ...(wantsGroupMove ? { groupIds: memberIds } : {}),
        };
        e.dataTransfer.setData("application/json", JSON.stringify(payload));
        // effectAllowed = "all" so macOS Chrome's Shift-held coercion to
        // "link" still resolves to a valid drop instead of silently aborting.
        e.dataTransfer.effectAllowed = "all";
        e.currentTarget.style.opacity = "0.35";
        if (onDragItemStart) onDragItemStart({ ...payload, color });
      }}
      onDragEnd={(e) => {
        e.currentTarget.style.opacity = "1";
        if (onDragItemStart) onDragItemStart(null);
      }}
      data-testid={`bblock-${session.id}`}
      data-block-ref={session._recurring_block_id || ""}
      className={`absolute select-none rounded cursor-grab active:cursor-grabbing overflow-hidden hover:z-20 group ${
        isConflict ? "ring-2 ring-[#B85C5C]/60 z-10" : "z-0"
      } ${session._isNew ? "ring-1 ring-[#1E3F33]/40" : ""} ${
        isArmedGroup ? "ring-2 ring-[#1E3F33]/50 z-10" : ""
      } ${
        isInGroup && shiftHeld && !isArmedGroup ? "ring-2 ring-[#1E3F33]/60 z-10" : ""
      } ${
        isValidationFlagged && !isConflict
          ? "ring-2 ring-[#D9A05B]/80 z-10 outline-dashed outline-2 outline-[#D9A05B]/60 outline-offset-[-1px]"
          : ""
      } ${
        highlightBlockId && session._recurring_block_id === highlightBlockId
          ? "ring-2 ring-[#D9A05B] animate-pulse z-10"
          : ""
      }`}
      style={{
        left: `${Math.max(0, Math.min(100, pct(sm)))}%`,
        width: `${Math.max(0.5, Math.min(100, (dur / TOTAL_MIN) * 100))}%`,
        top: `${Math.max(0, Math.min(100 - 1, (Number.isFinite(session._lane) ? session._lane : 0) * laneH + 2))}%`,
        height: `${Math.max(8, laneH - 4)}%`,
        minHeight: "26px",
        backgroundColor: color.bg,
        borderLeft: `3px solid ${color.border}`,
      }}
    >
      <div className="px-1.5 py-0.5 h-full flex flex-col justify-center relative pr-3">
        {isConflict && (
          <div className="absolute -top-0.5 right-2 w-3 h-3 rounded-full bg-[#B85C5C] flex items-center justify-center z-10">
            <Warning size={7} weight="fill" className="text-white" />
          </div>
        )}
        {isInGroup && (
          <span
            data-testid={`bblock-group-badge-${session.id}`}
            className={`absolute bottom-0.5 right-1 text-[9px] font-bold tabular-nums leading-none px-1 py-0.5 rounded-sm border transition-colors ${
              isArmedGroup || shiftHeld
                ? "bg-[#1E3F33] text-white border-[#1E3F33]"
                : "bg-white/85 text-[#4B5563] border-[#d4d2cc]"
            }`}
            title={`Group of ${memberIds.length} · Shift+drag or right-click → Move as group`}
          >
            ·{memberIds.length}
          </span>
        )}
        <p className="text-[10px] font-semibold leading-tight truncate" style={{ color: color.text }}>
          {label}
        </p>
        {dur >= 15 && (
          <p className="text-[9px] leading-none mt-0.5 text-[#6B7280] truncate">
            {dur}m
            {session.room?.name ? ` · ${session.room.name}` : ""}
            {cadenceLabel ? ` · ${cadenceLabel}` : ""}
          </p>
        )}
      </div>

      {onResize && (
        <div
          onMouseDown={handleResizeStart}
          draggable={false}
          data-testid={`resize-${session.id}`}
          className="absolute top-0 bottom-0 right-0 w-1.5 cursor-ew-resize bg-transparent hover:bg-[#1E3F33]/15 transition-colors flex items-center justify-center group-hover:bg-[#1E3F33]/10"
          title="Drag to resize"
        >
          <div className="w-0.5 h-3 bg-[#6B7280]/40 rounded-full opacity-0 group-hover:opacity-100" />
        </div>
      )}
    </div>
  );

  // No delete handler? Render the bare block (no right-click menu).
  if (!onDelete) return blockEl;

  const startLabel = fmtTime(session.start_time);
  const endLabel = fmtTime(session.end_time);
  const studentObj = session.students?.[0];
  const studentName = studentObj
    ? `${studentObj.first_name} ${studentObj.last_name || ""}`.trim()
    : session.session_type === "open"
    ? "Open / Consultation"
    : "Unassigned";
  // FP3-035B correction — Kindergarten = "0", no special "Kinder" label.
  const grade = studentObj?.grade ? `${studentObj.grade === "K" ? "0" : studentObj.grade}` : "—";
  // FP3-108 — Canonical classroom teacher resolution. Always read
  // from the STUDENT's Scheduling Profile (`teacher` — the canonical
  // display name — with an email-derived fallback that mirrors the
  // backend `_derive_teacher_display`). NEVER substitute the schedule
  // provider, assistant, supervising provider, or block creator.
  // Historical block-level snapshots (if any exist) are intentionally
  // ignored in favor of the CURRENT student teacher so refresh reflects
  // the profile immediately without a bulk migration.
  const teacher = (() => {
    if (!studentObj) return "—";
    const name = (studentObj.teacher || "").trim();
    if (name) return name;
    const derived = deriveTeacherDisplay(studentObj.teacher_email || "");
    return derived || "—";
  })();
  const roomName = session.room?.name || "—";
  const therapy = session.service_type || "—";
  const tags = [
    session.is_recurring ? "recurring" : null,
    session.session_type && session.session_type !== "individual" ? session.session_type : null,
  ]
    .filter(Boolean)
    .join(", ") || "—";

  const Row = ({ label, value, mono = false }) => (
    <div className="flex gap-2 items-baseline">
      <span className="text-[9px] font-semibold uppercase tracking-wider text-[#6B7280] min-w-[54px]">{label}</span>
      <span className={`text-[11px] text-[#1F2937] ${mono ? "tabular-nums" : ""} truncate`}>{value}</span>
    </div>
  );

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild data-testid={`block-trigger-${session.id}`}>
        {blockEl}
      </ContextMenuTrigger>
      <ContextMenuContent
        data-testid={`block-context-menu-${session.id}`}
        className="min-w-[240px] p-0 overflow-hidden"
      >
        {/* Information header */}
        <div className="bg-[#F3F1EC] px-3 py-2 border-b border-[#E5E3DB]">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-[#4B5563]">
            Information
          </p>
        </div>
        <div className="px-3 py-2.5 space-y-1.5" data-testid={`block-info-${session.id}`}>
          <Row label="Student" value={studentName} />
          <Row label="Length" value={`${dur} minutes`} mono />
          <Row label="Start" value={startLabel} mono />
          <Row label="End" value={endLabel} mono />
          <Row label="Teacher" value={teacher} />
          {!isInGroup && <Row label="Room" value={roomName} />}
          <Row label="Grade" value={grade} />
          <Row label={isInGroup ? "Type" : "Therapy"} value={therapy} />
          <Row label="Identifiers" value={tags} />
        </div>
        <ContextMenuSeparator className="my-0" />
        {isInGroup && onArmGroup && (
          <ContextMenuItem
            data-testid={`move-group-${session.id}`}
            onSelect={() => onArmGroup(memberIds)}
            className="text-[#1E3F33] focus:text-[#1E3F33] focus:bg-[#E8F0EB]"
          >
            <UsersThree size={13} weight="bold" className="mr-2" />
            Move as group ({memberIds.length})
          </ContextMenuItem>
        )}
        {studentObj && (
          <ContextMenuItem
            data-testid={`info-${session.id}`}
            onSelect={() => navigate(`/students/${studentObj.id}`)}
            className="text-[#1E3F33] focus:text-[#1E3F33] focus:bg-[#E8F0EB]"
          >
            <ArrowRight size={13} weight="bold" className="mr-2" />
            Student info
          </ContextMenuItem>
        )}
        <ContextMenuItem
          data-testid={`delete-${session.id}`}
          onSelect={() => onDelete(session)}
          className="text-[#B85C5C] focus:text-[#B85C5C] focus:bg-[#FDF2F2]"
        >
          <Trash size={13} weight="bold" className="mr-2" />
          Remove block
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}

// Build a smart label that prefers showing all member first names but falls back to
// "FirstName +N" only when the count is large.
function _buildMemberLabel(students, maxCount = 4) {
  if (!students || students.length === 0) return "";
  if (students.length <= maxCount) {
    return students.map((s) => s.first_name).join(", ");
  }
  return `${students[0].first_name} +${students.length - 1}`;
}

export function ViewSessionCard({ session, conflictSet, onLogged }) {
  const navigate = useShadowNavigate();
  const readOnly = useReadOnlyAsProvider();
  const [logOpen, setLogOpen] = useState(false);
  const isPersonal = session.block_kind === "personal";
  const color = isPersonal
    ? providerBlockColor(session.personal_kind)
    : gc(session.students);
  const isConflict = conflictSet.has(session.id);
  const isGrouped = !!session.is_grouped;
  const isCompleted = (session.status || "").toLowerCase() === "completed";
  // FP3-034E — Provider unavailability annotation. Sessions inside a
  // sick/meeting/etc. window stay visible but render muted with a
  // treatment chip; live-session launch is disabled because the
  // provider is OOO.
  const unavail = unavailMeta(session);
  // Iter 45p — Log Session is only meaningful for past or today's
  // scheduled sessions. Hide for future dates and personal/already
  // completed slots.
  // FP3-035F Issue 2 — Also hide in read-only shadow mode so admins
  // can't log session data under a provider they don't own.
  const today = localDateStr();
  const isFuture = (session.date || "") > today;
  const canLog = !isPersonal && !isCompleted && !isFuture && !unavail && !readOnly.active;
  // Iter 24 — monthly cadence indicator
  const wom = session.weeks_of_month;
  const cadenceLabel =
    Array.isArray(wom) && wom.length > 0 && wom.length < 4
      ? `${wom.length} of 4 weeks`
      : null;
  // Day-mode card has more horizontal space — show full names up to 6.
  const studentLabel = isPersonal
    ? (session.personal_label || session.personal_kind || "Personal")
    : session.students?.length > 0
      ? session.students.length <= 6
        ? session.students.map((s) => `${s.first_name} ${s.last_name}`).join(", ")
        : `${session.students.slice(0, 3).map((s) => s.first_name).join(", ")} +${session.students.length - 3} more`
      : session.service_type;
  const handleClick = () => {
    // Iter 33d Follow-up 11 — Calendar click always routes to Live
    // Data (regardless of grouped vs individual). Skips re-launching
    // already-completed sessions (open Session History instead) and
    // skips personal blocks (no Live Data surface).
    if (isPersonal) return;
    // FP3-035B-2 runtime guard (iter 63) — Mirror the ViewWeekCard
    // guard: if the Log Session modal is open, the day-view card
    // must NOT navigate to Live Session just because a Radix
    // SelectItem click happens to bubble through the Portal. The
    // dialog's own stopPropagation handlers cover most cases; this
    // is the second layer of protection.
    if (logOpen) return;
    // FP3-034E — Provider unavailable. Don't launch a live session
    // for a slot the provider is OOO on; clicking opens the student
    // page instead so the provider can review the make-up plan.
    if (unavail) {
      if (session.students?.[0]) navigate(`/students/${session.students[0].id}`);
      return;
    }
    if (isCompleted) {
      if (session.students?.[0]) {
        navigate(`/students/${session.students[0].id}?tab=sessions`);
      }
      return;
    }
    // FP3-035F Issue 2 — Skip Live Session launch in read-only mode;
    // open the student workspace instead.
    if (readOnly.active) {
      if (session.students?.[0]) navigate(`/students/${session.students[0].id}`);
      return;
    }
    if (session.session_ids?.length > 0) {
      navigate(`/session?launch=${session.session_ids.join(",")}`);
    } else if (typeof session.id === "string") {
      navigate(`/session?launch=${session.id}`);
    }
  };
  return (
    <div
      data-testid={`view-card-${session.id}`}
      data-session-status={session.status}
      data-provider-unavailable={unavail ? "true" : "false"}
      data-unavail-treatment={unavail?.treatment || ""}
      className={`flex items-center gap-4 p-3.5 rounded-xl border transition-all group ${
        unavail
          ? `${unavail.palette.cardBg} ${unavail.palette.cardBorder} ${unavail.palette.cardOpacity} hover:shadow-sm`
          : "bg-white border-[#E5E3DB]"
      } ${
        isConflict ? "ring-1 ring-[#B85C5C]/40" : ""
      } ${
        isPersonal
          ? "cursor-default"
          : isCompleted
          ? "cursor-pointer hover:border-[#1E5A3F]/30"
          : "cursor-pointer hover:shadow-sm hover:border-[#8FA998]/40"
      }`}
      onClick={handleClick}
    >
      <div
        className="w-3 rounded-full self-stretch"
        style={{ backgroundColor: color.border }}
      />
      <div className="text-right min-w-[72px]">
        <p className="text-sm font-medium text-[#1F2937]">{fmtTime(session.start_time)}</p>
        <p className="text-xs text-[#6B7280]">{fmtTime(session.end_time)}</p>
      </div>
      <Separator orientation="vertical" className="h-10 bg-[#E5E3DB]" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[#1F2937] truncate">{studentLabel}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {!isPersonal && (
            <SessionStatusChip
              status={session.status}
              attendance={session.attendance}
              sessionOutcome={session.session_outcome}
            />
          )}
          {unavail && (
            <UnavailableBadge session={session} size="sm" />
          )}
          <Badge
            variant="outline"
            className={`text-[10px] px-1.5 py-0 ${
              isGrouped ? "border-[#1E3F33]/30 bg-[#E8F0EB] text-[#1E3F33]" : "border-[#E5E3DB] text-[#6B7280]"
            }`}
            data-testid={isGrouped ? "view-group-badge" : "view-type-badge"}
          >
            {isGrouped ? `group · ${session.students.length}` : session.session_type}
          </Badge>
          {session.service_type && (
            <span className="text-[10px]" style={{ color: color.text }}>
              {session.service_type}
            </span>
          )}
          {cadenceLabel && (
            <span
              data-testid={`view-cadence-${session.id}`}
              className="text-[10px] px-1.5 py-0 rounded-full bg-[#E1EAE4] text-[#1E3F33] font-medium"
            >
              {cadenceLabel}
            </span>
          )}
          {session.room?.name && (
            <span className="text-[10px] text-[#6B7280] flex items-center gap-0.5">
              <MapPin size={10} />
              {session.room.name}
            </span>
          )}
        </div>
      </div>
      {isConflict && <Warning size={16} weight="fill" className="text-[#B85C5C] shrink-0" />}
      {canLog && (
        <button
          type="button"
          data-testid={`view-log-session-btn-${session.id}`}
          onClick={(e) => {
            e.stopPropagation();
            setLogOpen(true);
          }}
          className="text-[11px] font-medium text-[#1E3F33] border border-[#1E3F33]/30 rounded-full px-2.5 py-1 hover:bg-[#E8F0EB] transition-colors flex items-center gap-1 shrink-0"
          title="Log this session after the fact"
        >
          <ClipboardText size={12} weight="duotone" />
          Log Session
        </button>
      )}
      <ArrowRight size={14} className="text-[#6B7280] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
      {canLog && (
        <LogSessionDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          session={session}
          onLogged={() => {
            setLogOpen(false);
            if (onLogged) onLogged();
          }}
        />
      )}
    </div>
  );
}

export function ViewWeekCard({ session, conflictSet, onLogged }) {
  const navigate = useShadowNavigate();
  const readOnly = useReadOnlyAsProvider();
  const [logOpen, setLogOpen] = useState(false);
  const isPersonal = session.block_kind === "personal";
  const color = isPersonal
    ? providerBlockColor(session.personal_kind)
    : gc(session.students);
  const isConflict = conflictSet.has(session.id);
  const isGrouped = !!session.is_grouped;
  const isCompleted = (session.status || "").toLowerCase() === "completed";
  // FP3-034E — Provider unavailability annotation. Same treatment as
  // ViewSessionCard so the week column reads consistently with day mode.
  const unavail = unavailMeta(session);
  // Iter 45u — Log Session is loggable on past-or-today, non-personal,
  // non-completed cards. Keeps the same date discipline as the day-mode
  // ViewSessionCard so week mode behaves consistently.
  // FP3-035F Issue 2 — Hide in read-only shadow mode.
  const todayStr = localDateStr();
  const isFuture = (session.date || "") > todayStr;
  const canLog = !isPersonal && !isCompleted && !isFuture && !unavail && !readOnly.active;
  // Iter 24 — monthly cadence indicator
  const wom = session.weeks_of_month;
  const cadenceLabel =
    Array.isArray(wom) && wom.length > 0 && wom.length < 4
      ? `${wom.length} of 4 weeks`
      : null;
  // Week-column cards are narrow — show up to 4 first names then collapse.
  const label = isPersonal
    ? (session.personal_label || session.personal_kind || "Personal")
    : session.students?.length > 0
      ? _buildMemberLabel(session.students, 4)
      : session.service_type;
  const handleClick = () => {
    // Iter 33d Follow-up 11 — Same routing rule as ViewDayCard.
    if (isPersonal) return;
    // FP3-035B-2 runtime guard — If the Log Session modal is open
    // (the user clicked the Log Session icon), DO NOT navigate to
    // the Live Session page just because a SelectItem click leaked
    // through the Radix Portal back to this card. The dialog
    // handler covers its own UX; the card stays put until the
    // modal closes.
    if (logOpen) return;
    // FP3-034E — Don't launch a live session for an OOO slot.
    if (unavail) {
      if (session.students?.[0]) navigate(`/students/${session.students[0].id}`);
      return;
    }
    if (isCompleted) {
      if (session.students?.[0]) {
        navigate(`/students/${session.students[0].id}?tab=sessions`);
      }
      return;
    }
    // FP3-035F Issue 2 — Don't launch Live Session in read-only
    // shadow mode; open the student workspace instead.
    if (readOnly.active) {
      if (session.students?.[0]) navigate(`/students/${session.students[0].id}`);
      return;
    }
    if (session.session_ids?.length > 0) {
      navigate(`/session?launch=${session.session_ids.join(",")}`);
    } else if (typeof session.id === "string") {
      navigate(`/session?launch=${session.id}`);
    }
  };
  // Iter 33d Follow-up 14d/14e — Completed cards keep their original
  // grade-color tint; the dashed left accent bar and the prominent
  // "Completed" chip are the completion signals (NOT a uniform muted
  // background — that flattened all grade colors and the user
  // explicitly rejected it).
  //
  // FP3-034E polish — Unavailable cards drop the grade tint entirely
  // and switch to the treatment palette: warm-grey card + sage chip
  // for excused-no-service, warm-amber card + amber chip for
  // needs-make-up. A thicker (4px) left accent + the diagonal
  // out-of-service stripe overlay makes them readable at a glance
  // even before the chip is parsed.
  const cardStyle = unavail
    ? {
        borderLeft: `4px solid ${unavail.palette.cardAccent}`,
        backgroundImage: unavail.palette.stripePattern,
      }
    : { backgroundColor: color.bg, borderLeft: `3px ${isCompleted ? "dashed" : "solid"} ${color.border}` };
  return (
    <div
      data-testid={`vweek-card-${session.id}`}
      data-session-status={session.status}
      data-provider-unavailable={unavail ? "true" : "false"}
      data-unavail-treatment={unavail?.treatment || ""}
      className={`relative p-2.5 rounded-lg border transition-all ${
        unavail
          ? `${unavail.palette.cardBg} ${unavail.palette.cardBorder} ${unavail.palette.cardOpacity}`
          : ""
      } ${
        isConflict ? "border-[#B85C5C]/40 ring-1 ring-[#B85C5C]/20" : unavail ? "" : "border-[#E5E3DB] hover:border-[#8FA998]/40"
      } ${
        isPersonal
          ? "cursor-default"
          : isCompleted
          ? "cursor-pointer hover:border-[#1E5A3F]/40"
          : "cursor-pointer hover:shadow-sm"
      }`}
      style={cardStyle}
      onClick={handleClick}
    >
      {unavail && (
        <div className="mb-1.5 flex items-center justify-between gap-1">
          <UnavailableBadge session={session} size="xs" />
        </div>
      )}
      {!isPersonal && isCompleted && !unavail && (
        <div className="mb-1">
          <SessionStatusChip
            status={session.status}
            attendance={session.attendance}
            sessionOutcome={session.session_outcome}
            size="xs"
          />
        </div>
      )}
      {canLog && (
        <button
          type="button"
          data-testid={`vweek-log-session-btn-${session.id}`}
          onClick={(e) => {
            e.stopPropagation();
            setLogOpen(true);
          }}
          className="absolute top-1 right-1 z-10 inline-flex items-center justify-center w-5 h-5 rounded-md bg-white/80 hover:bg-white border border-[#1E3F33]/30 text-[#1E3F33] hover:shadow-sm transition"
          title="Log this session"
          aria-label="Log Session"
        >
          <ClipboardText size={11} weight="duotone" />
        </button>
      )}
      <div className="flex items-start gap-1">
        {isGrouped && (
          <span
            className="text-[8px] font-bold tabular-nums px-1 rounded shrink-0 bg-[#1E3F33]/15 mt-0.5"
            style={{ color: color.text }}
          >
            {session.students.length}
          </span>
        )}
        <p
          className={`text-[11px] font-semibold leading-tight line-clamp-2 ${
            unavail ? "text-[#4A4540]" : ""
          }`}
          style={unavail ? undefined : { color: color.text }}
        >
          {label}
        </p>
      </div>
      <div className="flex items-center gap-1 mt-0.5">
        <span className={`text-[10px] tabular-nums ${unavail ? "text-[#6E6859]" : "text-[#6B7280]"}`}>{fmtTime(session.start_time)}</span>
        <span className={`text-[10px] ${unavail ? "text-[#6E6859]" : "text-[#6B7280]"}`}>&middot;</span>
        <span
          className="text-[10px]"
          style={unavail ? { color: "#6E6859" } : { color: color.text + "90" }}
        >
          {isGrouped ? "group" : session.service_type}
        </span>
      </div>
      {cadenceLabel && (
        <p
          data-testid={`vweek-cadence-${session.id}`}
          className="text-[9px] mt-0.5 text-[#1E3F33] font-medium"
        >
          {cadenceLabel}
        </p>
      )}
      {session.room?.name && <p className="text-[9px] text-[#6B7280] truncate mt-0.5">{session.room.name}</p>}
      {canLog && (
        <LogSessionDialog
          open={logOpen}
          onOpenChange={setLogOpen}
          session={session}
          onLogged={() => {
            setLogOpen(false);
            if (onLogged) onLogged();
          }}
        />
      )}
    </div>
  );
}
